import { Components, Formio } from 'formiojs';
import FieldComponent from 'formiojs/components/_classes/field/Field';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';

export class BarcodeReaderComponent extends FieldComponent {
  private codeReader: BrowserMultiFormatReader;
  private scanning = false;
  private stream: MediaStream | null = null;
  refs: any;
  loadRefs: any;
  addEventListener: any;
  element: any;
  component: any;
  updateValue: any;

  static schema(...extend: any[]) {
    return FieldComponent.schema(
      {
        type: 'barcodereader',
        label: 'Barcode Reader',
        key: 'barcodereader',
        input: true,
        scannerWidth: 300,
        scannerHeight: 200,
        autoStart: false,
        continuous: false,
        formats: ['QR_CODE', 'CODE_128', 'CODE_39', 'EAN_13', 'EAN_8']
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: 'Barcode Reader',
      icon: 'qrcode',
      group: 'advanced',
      weight: 75,
      schema: BarcodeReaderComponent.schema(),
    };
  }

  constructor(component: any, options: any, data: any) {
    super(component, options, data);
    this.codeReader = new BrowserMultiFormatReader();
  }

  render(children: any) {
    const width = this.component.scannerWidth || 300;
    const height = this.component.scannerHeight || 200;

    return super.render(`
      <div class="barcode-reader-container">
        <div class="barcode-scanner" style="width: ${width}px; height: ${height}px; margin: 10px 0; border: 2px dashed #ddd; display: flex; align-items: center; justify-content: center; background: #f9f9f9; position: relative;">
          <video ref="barcodeVideo" style="width: 100%; height: 100%; object-fit: cover; display: none;"></video>
          <div ref="scannerPlaceholder" style="text-align: center; color: #666;">
            <i class="fa fa-qrcode" style="font-size: 32px; margin-bottom: 10px; display: block;"></i>
            <div>Click "Start Scanner" to scan barcode</div>
          </div>
          <div ref="scannerOverlay" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 200px; height: 120px; border: 3px solid #ff0000; border-radius: 10px; pointer-events: none; display: none;"></div>
        </div>
        <div class="barcode-controls" style="margin: 10px 0;">
          <button ref="startBtn" type="button" class="btn btn-primary" style="margin-right: 10px;">
            <i class="fa fa-camera" style="margin-right: 5px;"></i>
            Start Scanner
          </button>
          <button ref="stopBtn" type="button" class="btn btn-danger" style="display: none;">
            <i class="fa fa-stop" style="margin-right: 5px;"></i>
            Stop Scanner
          </button>
          <button ref="clearBtn" type="button" class="btn btn-secondary" style="margin-left: 10px;">
            <i class="fa fa-trash" style="margin-right: 5px;"></i>
            Clear
          </button>
        </div>
        <div class="barcode-result" style="margin: 10px 0;">
          <label style="font-weight: bold; margin-bottom: 5px; display: block;">Scanned Result:</label>
          <input ref="resultInput" type="text" class="form-control" name="${this.component.key}" placeholder="Scanned barcode will appear here..." readonly />
        </div>
        <div ref="infoDiv" style="margin-top: 10px; font-size: 12px; color: #666; display: none;">
          <div><strong>Format:</strong> <span ref="formatSpan"></span></div>
          <div><strong>Timestamp:</strong> <span ref="timestampSpan"></span></div>
        </div>
      </div>
    `);
  }

  attach(element: any) {
    const refs = super.attach(element);
    this.element = element;
    this.loadRefs(element, {
      barcodeVideo: 'single',
      scannerPlaceholder: 'single',
      scannerOverlay: 'single',
      startBtn: 'single',
      stopBtn: 'single',
      clearBtn: 'single',
      resultInput: 'single',
      infoDiv: 'single',
      formatSpan: 'single',
      timestampSpan: 'single'
    });

    if (this.refs.startBtn) {
      this.addEventListener(this.refs.startBtn, 'click', () => {
        this.startScanning();
      });
    }

    if (this.refs.stopBtn) {
      this.addEventListener(this.refs.stopBtn, 'click', () => {
        this.stopScanning();
      });
    }

    if (this.refs.clearBtn) {
      this.addEventListener(this.refs.clearBtn, 'click', () => {
        this.clearResult();
      });
    }

    if (this.component.autoStart) {
      setTimeout(() => {
        this.startScanning();
      }, 1000);
    }

    return refs;
  }

  private async startScanning() {
    if (this.scanning) return;

    try {
      this.scanning = true;
      this.refs.startBtn.style.display = 'none';
      this.refs.stopBtn.style.display = 'inline-block';
      this.refs.scannerPlaceholder.style.display = 'none';
      this.refs.barcodeVideo.style.display = 'block';
      this.refs.scannerOverlay.style.display = 'block';

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });

      this.refs.barcodeVideo.srcObject = this.stream;
      await this.refs.barcodeVideo.play();

      this.codeReader.decodeFromVideoDevice(undefined, this.refs.barcodeVideo, (result, error) => {
        if (result) {
          const scannedText = result.getText();
          const format = result.getBarcodeFormat();
          const timestamp = new Date().toLocaleString();

          this.refs.resultInput.value = scannedText;
          this.updateComponentValue(scannedText);

          if (this.refs.formatSpan) this.refs.formatSpan.textContent = format.toString();
          if (this.refs.timestampSpan) this.refs.timestampSpan.textContent = timestamp;
          this.refs.infoDiv.style.display = 'block';

          if (!this.component.continuous) {
            this.stopScanning();
          }

          this.showSuccessFeedback();
        }

        if (error && !(error instanceof NotFoundException)) {
          console.warn('Barcode scanning error:', error);
        }
      });

    } catch (error) {
      console.error('Error starting barcode scanner:', error);
      this.showErrorFeedback('Camera access denied or not available');
      this.stopScanning();
    }
  }

  private stopScanning() {
    if (!this.scanning) return;

    this.scanning = false;
    this.codeReader.reset();

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    this.refs.barcodeVideo.style.display = 'none';
    this.refs.scannerOverlay.style.display = 'none';
    this.refs.scannerPlaceholder.style.display = 'flex';
    this.refs.startBtn.style.display = 'inline-block';
    this.refs.stopBtn.style.display = 'none';
  }

  private clearResult() {
    this.refs.resultInput.value = '';
    this.refs.infoDiv.style.display = 'none';
    this.updateComponentValue('');
  }

  private showSuccessFeedback() {
    const container = this.element?.querySelector('.barcode-reader-container');
    if (container) {
      container.style.border = '2px solid #28a745';
      setTimeout(() => {
        container.style.border = '';
      }, 1000);
    }
  }

  private showErrorFeedback(message: string) {
    const container = this.element?.querySelector('.barcode-reader-container');
    if (container) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'alert alert-danger';
      errorDiv.style.marginTop = '10px';
      errorDiv.textContent = message;
      container.appendChild(errorDiv);
      
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      }, 3000);
    }
  }

  getValue() {
    return this.refs.resultInput ? this.refs.resultInput.value : '';
  }

  updateComponentValue(value: any) {
    if (this.refs.resultInput) {
      this.refs.resultInput.value = value || '';
    }
    this.updateValue(value, { modified: true });
  }

  destroy() {
    if (this.scanning) {
      this.codeReader.reset();
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    super.destroy();
  }
}